import { useNavigate, useSearch } from "@tanstack/react-router";
import { api } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   What the Uploader pages read from /api/m/uploader, typed once.

   The shapes mirror modules/uploader/server/routes.ts. The web half never
   imports the server half, so they are written out by hand here.
   ────────────────────────────────────────────────────────────────────────── */

export const BASE = "/api/m/uploader";

/** One flow's work in the engine (server/engine/queue-view.ts FlowQueueRow). */
export type FlowQueueRow = {
  flowId: string | null;
  creator: string;
  category: string;
  source: string;
  made: number;
  left: number;
  running: number;
  queued: number;
  stuck: number;
  lastOutputAt: string | null;
  etaMinutes: number | null;
  firstQueuedAt: string | null;
};
export type StuckTask = { id: string; creator: string; category: string; fileName: string; error: string; attempts: number; at: string };
export type RenderBeat = { at: string; slots: number; busy: number };
export type QueueResponse = {
  rows: FlowQueueRow[];
  stuck: StuckTask[];
  /** How many are stuck for this view (the creator's, when filtered); `stuck` holds the newest 50. */
  stuckTotal: number;
  paused: boolean;
  perMinute: number;
  render: RenderBeat | null;
  counts: { queued: number; running: number; stuck: number };
};

/** One source file a flow has finished (server/engine/ledger-view.ts LedgerRow). */
export type LedgerRow = {
  flow_id: string;
  file_id: string;
  file_name: string | null;
  kind: string;
  done_at: string;
  task_id: string | null;
  /** "engine" when its duplicates were made, "skip" when a person skipped it. */
  via: string;
  creator: string | null;
  category: string | null;
  source_id: string | null;
};

/** Duplicates made over a range of Melbourne days (server/engine/ledger-view.ts OutputStats). */
export type UploadStats = {
  startDate: string;
  endDate: string;
  /** Creators with duplicates made in the range. */
  creatorsScanned: number;
  /** Source files with duplicates made in the range. */
  rowsIncluded: number;
  totals: { photos: number; videos: number; label: string };
  categories: { category: string; photos: number; videos: number; label: string; creators: { creator: string; photos: number; videos: number; label: string }[] }[];
  days: { day: string; creator: string; category: string; photos: number; videos: number }[];
  /** The summary the node editor's stats view offered to copy, one block per category. */
  message?: string;
};

/** A creator, with how many flows they have and how many of those run (server/engine/flows-repo.ts creatorSummaries). */
export type Creator = { name: string; flows: number; active_flows: number };

export type Folder = { id: string; name: string | null; link: string; exists: boolean | null };
export type FlowStep = { step: number; type: "source" | "duplicate" | "destination" | "move"; label: string; folder?: Folder | null; mediaType?: string };
export type Flow = {
  id: string;
  name: string;
  row: number | null;
  category: string | null;
  duplicatesPerItem: number;
  applyAdjustments: boolean;
  enabled: boolean;
  active: boolean;
  inactiveReason: string | null;
  options: { checkSubfolders: boolean; checkLeft: boolean; requireApproval: boolean };
  notes: string | null;
  warnings: string[];
  steps: FlowStep[];
};
/** `note` says why folders show by id, when Drive could not name them. */
export type FlowsResponse = { creator: Creator; flows: Flow[]; note: string | null };

export type Tone = "good" | "warn" | "bad" | "idle";
export type Source = "env" | "stored" | "missing";
export type EngineHealth = {
  render: RenderBeat | null;
  watch: string | null;
  sweep: string | null;
  paused: boolean;
  drive: { ok: boolean; error?: string; at: string | null };
  lease: { holder: string; until: string } | null;
  queued: number;
  running: number;
  stuck: number;
};
export type Health = {
  verdict: { tone: Tone; reasons: string[] };
  engine: EngineHealth;
  credentials: { driveOauth: Source; telegram: Source; publicKeys: Source };
};

export type ManualForm = {
  sources: string[];
  duplicatesPerItem: number;
  videoDestFolderLink?: string;
  photoDestFolderLink?: string;
  rawMoveFolderLink?: string;
  creator?: string;
  category?: string;
  recursive?: boolean;
  applyAdjustments?: boolean;
  forceReduplicate?: boolean;
  startDate: string;
  endDate: string;
  mediaMode: "both" | "videos" | "photos";
};
export type Preview = {
  previewId: string;
  preview: {
    ok: boolean;
    jobs: { label: string; videoCount: number; photoCount: number; selectedVideoCount: number; selectedPhotoCount: number; duplicateCount: number; truncated: boolean; skippedCount: number }[];
    errors: { index: number; message: string }[];
    totals: { videos: number; photos: number; duplications: number };
  };
};
/** One queued job is a batch of engine tasks; jobId is the batch id GET /jobs/status follows. */
export type Queued = {
  jobs: { jobId: string; label: string; tasks: number; skipped: number; videoCount: number; photoCount: number; duplicateCount: number; truncated: boolean }[];
  totals: { videos: number; photos: number; duplications: number; tasks: number };
};
export type JobStatus = { jobId: string; status: "WAITING" | "RUNNING" | "FAILED" | "DONE"; created: number; left: number; found: boolean };

/* ── query keys and requests ────────────────────────────────────────────── */

export const keys = {
  all: ["uploader"] as const,
  queue: (filters: Record<string, string | undefined>) => ["uploader", "queue", filters] as const,
  ledger: (filters: Record<string, string | undefined>) => ["uploader", "ledger", filters] as const,
  stats: (start: string, end: string) => ["uploader", "stats", start, end] as const,
  creators: ["uploader", "creators"] as const,
  flows: (name: string) => ["uploader", "flows", name] as const,
  health: ["uploader", "health"] as const,
  jobs: (ids: string[]) => ["uploader", "jobs", ids.join(",")] as const,
};

export const fetchQueue = (filters: { status?: string; creator?: string }) => api<QueueResponse>(`${BASE}/queue${qs(filters)}`);
export const queueApi = {
  retry: (id: string) => api<{ id: string; status: string }>(`${BASE}/queue/retry`, { body: { id } }),
  skip: (id: string) => api<{ id: string; status: string }>(`${BASE}/queue/skip`, { body: { id } }),
  pause: (paused: boolean) => api<{ paused: boolean }>(`${BASE}/queue/pause`, { body: { paused } }),
  first: (creator: string) => api<{ creator: string; moved: number }>(`${BASE}/queue/first`, { body: { creator } }),
};
export const fetchLedger = (filters: { q?: string; creator?: string; offset?: number }) =>
  api<{ rows: LedgerRow[]; more: boolean }>(`${BASE}/ledger${qs({ q: filters.q, creator: filters.creator, offset: filters.offset ? String(filters.offset) : undefined })}`);
export const fetchStats = (start: string, end: string) => api<{ stats: UploadStats; cachedAt: string }>(`${BASE}/stats${qs({ start, end })}`);
export const fetchCreators = () => api<{ creators: Creator[] }>(`${BASE}/creators`);
export const fetchFlows = (name: string) => api<FlowsResponse>(`${BASE}/creators/${encodeURIComponent(name)}/flows`);
export const fetchHealth = () => api<Health>(`${BASE}/health`);
export const fetchJobs = (ids: string[]) => api<{ jobs: JobStatus[] }>(`${BASE}/jobs/status${qs({ ids: ids.join(",") })}`);

/* ── the flow editor ────────────────────────────────────────────────────── */

/** One flow row as the editor reads and writes it; graphToRows makes these. */
export type SheetRow = {
  rowIndex: number | null;
  driveLink: string;
  checkSubfolders: boolean;
  checkLeft: boolean;
  requireApproval: boolean;
  duplicatesPerItem: number;
  videoDestFolderLink: string;
  photoDestFolderLink: string;
  rawMoveFolderLink: string;
  category: string;
  notes: string;
  applyAdjustments: boolean;
  enabled: boolean;
  /** The iPhone identity switch. Always read; on a write, absent keeps an existing flow's and turns a new one on. */
  iphoneIdentity?: boolean;
};

/** The creator's phone and place (server/editor.ts IphoneSettings, engine/iphone/place.ts PlaceSummary). */
export type IphonePlace = {
  city: string;
  state: string;
  status: "building" | "ready" | "failed";
  reason?: string;
  /** Nominatim's name for what it found. */
  display?: string;
  points: number;
  tz?: string;
  built_at?: string;
};
export type IphoneSettings = { creator: string; model: string | null; place: IphonePlace | null };

export type SavedGraph = { creator: string; graph: { nodes: any[]; edges: any[] } | null; revision: number; savedAt: number | null };
export type EditorManualRequest = { jobs: Record<string, unknown>[]; startDate: string; endDate: string; mediaMode: "both" | "videos" | "photos" };

export const editorApi = {
  creators: () => api<{ creators: string[] }>(`${BASE}/editor/creators`),
  rows: (creator: string) => api<{ creator: string; rows: SheetRow[] }>(`${BASE}/editor/rows${qs({ creator })}`),
  saveRows: (creator: string, rows: SheetRow[]) => api<{ creator: string; written: number; revision: number }>(`${BASE}/editor/rows`, { method: "PUT", body: { creator, rows } }),
  graph: (creator: string) => api<SavedGraph>(`${BASE}/editor/graph${qs({ creator })}`),
  saveGraph: (creator: string, graph: unknown) => api<{ creator: string; revision: number }>(`${BASE}/editor/graph`, { method: "PUT", body: { creator, graph } }),
  categories: () => api<{ categories: string[] }>(`${BASE}/editor/categories`),
  addCategory: (name: string) => api<{ categories: string[] }>(`${BASE}/editor/categories`, { body: { name } }),
  folderName: (id: string, creator: string) => api<{ id: string; label: string }>(`${BASE}/editor/folder-name${qs({ id, creator })}`),
  manualPreview: (request: EditorManualRequest) => api<Preview>(`${BASE}/editor/manual/preview`, { body: request }),
  manualQueue: (request: EditorManualRequest, previewId: string) => api<Queued>(`${BASE}/editor/manual/queue`, { body: { ...request, previewId } }),
  eventsUrl: (creator: string) => `${BASE}/editor/events${qs({ creator })}`,
  iphone: (creator: string) => api<IphoneSettings>(`${BASE}/editor/iphone${qs({ creator })}`),
  saveIphone: (body: { creator: string; model?: string | null; place?: { city: string; state: string }; clearPlace?: boolean }) =>
    api<IphoneSettings & { queued: boolean }>(`${BASE}/editor/iphone`, { method: "PUT", body }),
};

export function qs(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1]));
  return entries.length ? `?${new URLSearchParams(entries).toString()}` : "";
}

/* ── search params ──────────────────────────────────────────────────────── */

/** A search param that is a non-empty string, or nothing. */
export function str(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/** Filters live in the URL, so a filtered queue is a link and the back button undoes a filter. */
export function useSearchState<T extends Record<string, string | undefined>>() {
  // The registered router's types only know the core routes; a module page reads its own search loosely.
  const search = useSearch({ strict: false } as never) as T;
  const navigate = useNavigate();
  const set = (patch: Partial<T>, options: { replace?: boolean } = {}) =>
    navigate({
      to: ".",
      search: ((prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        for (const key of Object.keys(next)) if (next[key] === undefined || next[key] === "") delete next[key];
        return next;
      }) as never,
      replace: options.replace ?? false,
    });
  return [search, set] as const;
}

/* ── labels ────────────────────────────────────────────────────────────── */

export const STATUS_LABEL: Record<string, string> = {
  WAITING: "Waiting",
  RUNNING: "Running",
  MOVING: "Moving originals",
  READY_TO_MOVE: "Ready to move",
  PAUSE_REQUESTED: "Pausing",
  PAUSED: "Paused",
  FAILED: "Failed",
  DONE: "Done",
  "": "No status",
  BLANK: "No status",
};

export const statusLabel = (status: string) => STATUS_LABEL[status] ?? status.replace(/_/g, " ").toLowerCase();

/** Today in Melbourne, where the engine's days are counted, as "2026-09-28". */
export const melbourneDay = (offsetDays = 0) => new Date(Date.now() + offsetDays * 86_400_000).toLocaleDateString("en-CA", { timeZone: "Australia/Melbourne" });

export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
